import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    TrendingUp,
    TrendingDown,
    Calendar,
    DollarSign,
    Users,
    Download,
    CreditCard,
    ChevronDown,
    ChevronUp,
    BarChart3
} from 'lucide-react';

const RevenueDetailsTable = ({ monthlyData, metrics, loading = false }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

    if (loading) {
        return (
            <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                            <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                        </div>
                        <span className="text-foreground/90 font-medium">Revenue Details</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        <div className="animate-pulse space-y-2">
                            <div className="h-4 bg-muted rounded w-3/4"></div>
                            <div className="h-4 bg-muted rounded w-1/2"></div>
                            <div className="h-4 bg-muted rounded w-2/3"></div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!monthlyData || monthlyData.length === 0) {
        return (
            <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                            <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                        </div>
                        <span className="text-foreground/90 font-medium">Revenue Details</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-sm">No revenue data available</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">Start generating revenue to see detailed breakdown</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const toggleRow = (index) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case 'date':
                    aValue = new Date(a.date);
                    bValue = new Date(b.date);
                    break;
                case 'revenue':
                    aValue = a.revenue;
                    bValue = b.revenue;
                    break;
                case 'downloads':
                    aValue = a.downloads;
                    bValue = b.downloads;
                    break;
                case 'subscriptions':
                    aValue = a.subscriptions;
                    bValue = b.subscriptions;
                    break;
                default:
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
            }

            if (sortConfig.direction === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortedData = sortData(monthlyData);

    // Calculate summary statistics
    const totalRevenue = sortedData.reduce((sum, item) => sum + item.revenue, 0);
    const totalDownloads = sortedData.reduce((sum, item) => sum + item.downloads, 0);
    const totalSubscriptions = sortedData.reduce((sum, item) => sum + item.subscriptions, 0);
    const avgRevenue = totalRevenue / sortedData.length;
    const avgDownloads = totalDownloads / sortedData.length;
    const avgSubscriptions = totalSubscriptions / sortedData.length;

    return (
        <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                    </div>
                    <span className="text-foreground/90 font-medium">Revenue Details</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {/* Summary Statistics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
                    <div className="text-center">
                        <div className="text-xs text-muted-foreground">Total Revenue</div>
                        <div className="text-lg font-semibold text-green-600">
                            ${Math.round(totalRevenue).toLocaleString()}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-muted-foreground">Avg Daily Revenue</div>
                        <div className="text-lg font-semibold">
                            ${Math.round(avgRevenue).toLocaleString()}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-muted-foreground">Total Downloads</div>
                        <div className="text-lg font-semibold text-blue-600">
                            {Math.round(totalDownloads).toLocaleString()}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-muted-foreground">Total Subscriptions</div>
                        <div className="text-lg font-semibold text-purple-600">
                            {Math.round(totalSubscriptions).toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* Detailed Table */}
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[100px]">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSort('date')}
                                        className="h-auto p-0 font-medium"
                                    >
                                        Date
                                        {sortConfig.key === 'date' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                                        )}
                                    </Button>
                                </TableHead>
                                <TableHead className="w-[120px]">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSort('revenue')}
                                        className="h-auto p-0 font-medium"
                                    >
                                        Revenue
                                        {sortConfig.key === 'revenue' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                                        )}
                                    </Button>
                                </TableHead>
                                <TableHead className="w-[100px]">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSort('downloads')}
                                        className="h-auto p-0 font-medium"
                                    >
                                        Downloads
                                        {sortConfig.key === 'downloads' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                                        )}
                                    </Button>
                                </TableHead>
                                <TableHead className="w-[120px]">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSort('subscriptions')}
                                        className="h-auto p-0 font-medium"
                                    >
                                        Subscriptions
                                        {sortConfig.key === 'subscriptions' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                                        )}
                                    </Button>
                                </TableHead>
                                <TableHead className="w-[100px]">Breakdown</TableHead>
                                <TableHead className="w-[80px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedData.map((item, index) => {
                                const isExpanded = expandedRows.has(index);
                                const downloadRevenue = item.downloads * 4.99;
                                const subscriptionRevenue = item.subscriptions * 9.99;
                                const downloadPercentage = item.revenue > 0 ? (downloadRevenue / item.revenue) * 100 : 0;
                                const subscriptionPercentage = item.revenue > 0 ? (subscriptionRevenue / item.revenue) * 100 : 0;

                                return (
                                    <React.Fragment key={index}>
                                        <TableRow className="hover:bg-muted/50">
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-3 w-3 text-muted-foreground" />
                                                    {item.date}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-semibold text-green-600">
                                                    ${Math.round(item.revenue).toLocaleString()}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Download className="h-3 w-3 text-blue-500" />
                                                    {Math.round(item.downloads)}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <CreditCard className="h-3 w-3 text-purple-500" />
                                                    {Math.round(item.subscriptions)}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Badge variant="secondary" className="text-xs">
                                                        {Math.round(downloadPercentage)}%
                                                    </Badge>
                                                    <Badge variant="outline" className="text-xs">
                                                        {Math.round(subscriptionPercentage)}%
                                                    </Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleRow(index)}
                                                    className="h-6 w-6 p-0"
                                                >
                                                    {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                                </Button>
                                            </TableCell>
                                        </TableRow>

                                        {/* Expanded Row Details */}
                                        {isExpanded && (
                                            <TableRow>
                                                <TableCell colSpan={6} className="bg-muted/20">
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="p-4 space-y-4"
                                                    >
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                            <div className="space-y-2">
                                                                <h4 className="text-sm font-medium text-blue-600 flex items-center gap-1">
                                                                    <Download className="h-3 w-3" />
                                                                    Download Revenue
                                                                </h4>
                                                                <div className="text-lg font-semibold">
                                                                    ${Math.round(downloadRevenue).toLocaleString()}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {Math.round(item.downloads)} downloads × $4.99
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <h4 className="text-sm font-medium text-purple-600 flex items-center gap-1">
                                                                    <CreditCard className="h-3 w-3" />
                                                                    Subscription Revenue
                                                                </h4>
                                                                <div className="text-lg font-semibold">
                                                                    ${Math.round(subscriptionRevenue).toLocaleString()}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {Math.round(item.subscriptions)} subscriptions × $9.99
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <h4 className="text-sm font-medium text-green-600 flex items-center gap-1">
                                                                    <DollarSign className="h-3 w-3" />
                                                                    Total Revenue
                                                                </h4>
                                                                <div className="text-lg font-semibold">
                                                                    ${Math.round(item.revenue).toLocaleString()}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    Combined revenue for this day
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="pt-2 border-t border-border/50">
                                                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                                <span>Revenue Breakdown</span>
                                                                <span>100%</span>
                                                            </div>
                                                            <div className="mt-1 h-2 bg-muted rounded-full overflow-hidden">
                                                                <div className="flex h-full">
                                                                    <div
                                                                        className="bg-blue-500 h-full transition-all duration-300"
                                                                        style={{ width: `${downloadPercentage}%` }}
                                                                    />
                                                                    <div
                                                                        className="bg-purple-500 h-full transition-all duration-300"
                                                                        style={{ width: `${subscriptionPercentage}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="flex justify-between text-xs mt-1">
                                                                <span className="text-blue-600">Downloads: {Math.round(downloadPercentage)}%</span>
                                                                <span className="text-purple-600">Subscriptions: {Math.round(subscriptionPercentage)}%</span>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default RevenueDetailsTable; 